import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(undefined);

  // Legacy migration: if a returning user still has a pre-cookie `mp_token` in localStorage
  // (from before the httpOnly cookie deploy), attach it to the first /auth/me call so the
  // backend can mint a fresh cookie. Then clear it. This block reads localStorage on purpose
  // and is INTENTIONALLY narrow (one-time, read-only on a legacy field).
  useEffect(() => {
    const legacy = (() => {
      try { return localStorage.getItem("mp_token"); } catch (_) { return null; }
    })();
    const config = legacy ? { headers: { Authorization: `Bearer ${legacy}` } } : undefined;
    api
      .get("/auth/me", config)
      .then((r) => {
        setUser(r.data);
        if (legacy) {
          try { localStorage.removeItem("mp_token"); } catch (_) { /* noop */ }
        }
      })
      .catch(() => {
        setUser(null);
        try { localStorage.removeItem("mp_token"); } catch (_) { /* noop */ }
      });
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.requires_2fa) return data;
    setUser(data.user); // cookie is set by backend
    return { user: data.user };
  }, []);

  const verify2fa = useCallback(async (challenge_token, method, code) => {
    const { data } = await api.post("/auth/login/verify", { challenge_token, method, code });
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn("logout request failed", e);
      }
    }
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, login, verify2fa, logout }),
    [user, login, verify2fa, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
